import { useState, useMemo } from "react";
import {
  ChevronDown,
  ChevronUp,
  GitCompareArrows,
  Link2,
  Copy,
  Check,
} from "lucide-react";
import { tileCardStyle, tileHoverHandlers } from "../Wiki/utils";

/* ── Helpers ── */
const formatTimestamp = (ts) => {
  if (ts == null) return "";
  try {
    const num = typeof ts === "string" ? Number(ts) : ts;
    const date = new Date(
      typeof num === "number" && num < 1e12 ? num * 1000 : num,
    );
    if (isNaN(date.getTime())) return String(ts);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(ts);
  }
};

const stripCitations = (text) =>
  (text || "").replace(/\[\d+\](?:\[\d+\])*/g, "");

const applyInlineStyles = (text) => {
  if (!text) return null;
  const parts = [];
  const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let match,
    lastIndex = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    const raw = match[0];
    if (raw.startsWith("**"))
      parts.push(
        <strong
          key={match.index}
          className="font-semibold text-zinc-700 dark:text-zinc-200"
        >
          {raw.slice(2, -2)}
        </strong>,
      );
    else parts.push(<em key={match.index}>{raw.slice(1, -1)}</em>);
    lastIndex = match.index + raw.length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

const renderInline = (text) => applyInlineStyles(stripCitations(text));

const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      out.push(<div key={"gap-" + i} className="h-1.5" />);
      i++;
      continue;
    }
    if (/^-{3,}$/.test(line.trim())) {
      out.push(
        <hr
          key={"hr-" + i}
          className="border-t border-zinc-100 dark:border-zinc-800 my-2"
        />,
      );
      i++;
      continue;
    }
    if (line.startsWith("## ")) {
      out.push(
        <p
          key={"h2-" + i}
          className="text-[12px] font-semibold text-zinc-600 dark:text-zinc-300 mt-3 mb-0.5 uppercase tracking-wider"
        >
          {line.slice(3).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (line.startsWith("### ")) {
      out.push(
        <p
          key={"h3-" + i}
          className="text-[11px] font-semibold text-zinc-500 dark:text-zinc-400 mt-2 mb-0.5 uppercase tracking-wider"
        >
          {line.slice(4).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (line.startsWith("# ")) {
      out.push(
        <p
          key={"h1-" + i}
          className="text-[13px] font-semibold text-zinc-700 dark:text-zinc-200 mt-3 mb-1"
        >
          {line.slice(2).replace(/\*\*/g, "")}
        </p>,
      );
      i++;
      continue;
    }
    if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(lines[i].slice(2));
        i++;
      }
      out.push(
        <ul key={"ul-" + i} className="pl-2 flex flex-col gap-0.5 my-1">
          {items.map((item, idx) => (
            <li
              key={idx}
              className="flex gap-2 text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300"
            >
              <span className="mt-[7px] h-1 w-1 rounded-full bg-zinc-400 dark:bg-zinc-500 shrink-0" />
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ul>,
      );
      continue;
    }
    out.push(
      <p
        key={"p-" + i}
        className="text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-300"
      >
        {renderInline(line)}
      </p>,
    );
    i++;
  }
  return out;
};

const computeWordDiff = (oldText, newText) => {
  const ow = (oldText || "").split(/(\s+)/);
  const nw = (newText || "").split(/(\s+)/);
  const m = ow.length,
    n = nw.length;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] =
        ow[i - 1] === nw[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
  const result = [];
  let i = m,
    j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && ow[i - 1] === nw[j - 1]) {
      result.unshift({ type: "same", text: ow[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "added", text: nw[j - 1] });
      j--;
    } else {
      result.unshift({ type: "removed", text: ow[i - 1] });
      i--;
    }
  }
  return result;
};

/* ── Subcomponents ── */
const DiffView = ({ diffParts, t }) => {
  const oldParts = [],
    newParts = [];
  diffParts.forEach((p, i) => {
    if (p.type === "same") {
      oldParts.push(<span key={i}>{p.text}</span>);
      newParts.push(<span key={i}>{p.text}</span>);
    } else if (p.type === "removed")
      oldParts.push(
        <span
          key={i}
          className="bg-red-100 text-red-800 line-through dark:bg-red-900/30 dark:text-red-300"
        >
          {p.text}
        </span>,
      );
    else
      newParts.push(
        <span
          key={i}
          className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
        >
          {p.text}
        </span>,
      );
  });
  return (
    <div className="grid grid-cols-2 gap-3">
      <div
        style={{
          borderRight: `1px solid ${t.divider}`,
          color: t.textSecondary,
        }}
        className="pr-3"
      >
        <span
          style={{ color: t.textMuted }}
          className="text-[10px] uppercase tracking-wider font-medium block mb-2"
        >
          Previous
        </span>
        <p
          className="text-sm leading-relaxed whitespace-pre-wrap"
          style={{ margin: 0 }}
        >
          {oldParts}
        </p>
      </div>
      <div style={{ color: t.textSecondary }} className="pl-1">
        <span
          style={{ color: t.textMuted }}
          className="text-[10px] uppercase tracking-wider font-medium block mb-2"
        >
          Current
        </span>
        <p
          className="text-sm leading-relaxed whitespace-pre-wrap"
          style={{ margin: 0 }}
        >
          {newParts}
        </p>
      </div>
    </div>
  );
};

const SourcesDropdown = ({ sourcesList, t, accent }) => {
  const [open, setOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);
  const copy = async (e, url, key) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1600);
    } catch {}
  };
  const urls = (sourcesList || [])
    .map((s) => s?.value || s?.url || s?.doc_id)
    .filter(Boolean);
  if (urls.length === 0) return null;
  return (
    <div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{ color: open ? accent : t.textMuted }}
        className="flex items-center gap-1 text-[11px] font-medium hover:opacity-75 transition-all"
      >
        <Link2 className="h-3 w-3" strokeWidth={2} />
        {urls.length} source{urls.length !== 1 ? "s" : ""}
        <ChevronDown
          className="h-3 w-3 transition-transform duration-150"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          strokeWidth={2}
        />
      </button>
      {open && (
        <div
          className="mt-2 flex flex-col gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {urls.map((url, i) => {
            const isCopied = copiedKey === i;
            const display = url
              .replace(/^https?:\/\/(www\.)?/, "")
              .replace(/\/$/, "");
            return (
              <div
                key={i}
                style={{
                  background: t.labelBg,
                  border: `1px solid ${t.divider}`,
                }}
                className="flex items-center gap-2 rounded-lg px-3 py-1.5"
              >
                <span
                  style={{ color: t.textMuted }}
                  className="flex-1 min-w-0 text-[11px] font-mono truncate"
                  title={url}
                >
                  {display}
                </span>
                <button
                  onClick={(e) => copy(e, url, i)}
                  style={{ color: isCopied ? accent : t.textMuted }}
                  className="shrink-0 transition-colors hover:opacity-75"
                  title="Copy URL"
                >
                  {isCopied ? (
                    <Check className="h-3 w-3" strokeWidth={2.5} />
                  ) : (
                    <Copy className="h-3 w-3" strokeWidth={2} />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const VersionDropdown = ({ versions, selectedIdx, onSelect, t }) => {
  const [open, setOpen] = useState(false);
  const selected = versions[selectedIdx];
  return (
    <div className="relative shrink-0">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{
          background: t.labelBg,
          border: `1px solid ${t.labelBorder}`,
          color: t.labelText,
        }}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium"
      >
        {formatTimestamp(selected?.timestamp)}
        <ChevronDown className="h-3 w-3" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            style={{
              background: t.pageBg,
              border: `1px solid ${t.cardBorder}`,
            }}
            className="absolute right-0 top-full mt-1 z-20 rounded-md shadow-lg min-w-[160px] py-1"
          >
            {versions.map((v, idx) => (
              <button
                key={`${v.report_id || ""}-${idx}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(idx);
                  setOpen(false);
                }}
                style={{
                  color: idx === selectedIdx ? t.badgeText : t.textSecondary,
                  background: idx === selectedIdx ? t.badgeBg : "transparent",
                }}
                className="w-full text-left px-3 py-1.5 text-[11px] hover:opacity-80 transition-opacity"
              >
                {formatTimestamp(v.timestamp)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ── TileReport ── */
const COLLAPSE_THRESHOLD = 480;

const TileReport = ({
  sectionKey = "",
  versions = [],
  t,
  selectedIdx = 0,
  onSelectVersion,
}) => {
  const [showChanges, setShowChanges] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const selected = versions[selectedIdx] || versions[0];
  const previous = versions[selectedIdx + 1] || null;

  const diffParts = useMemo(() => {
    if (!showChanges || !previous || !selected) return null;
    return computeWordDiff(
      stripCitations(previous.content || ""),
      stripCitations(selected.content || ""),
    );
  }, [showChanges, selected, previous]);

  console.log("[TileReport]", {
    sectionKey,
    versionCount: versions.length,
    selectedIdx,
    hasSelected: !!selected,
    sectionTitle: selected?.section_title,
    contentLen: selected?.content?.length,
  });

  if (!selected) {
    console.log("[TileReport] → null (no selected)", { sectionKey });
    return null;
  }

  const sectionTitle = (selected.section_title || sectionKey || "").trim();
  const content = (selected.content || "").trim();
  const timestamp = formatTimestamp(selected.timestamp);
  const accent = t?.accent || "#0EA5E9";
  const isLong = content.length > COLLAPSE_THRESHOLD;
  const displayContent =
    isLong && !expanded && !showChanges
      ? content.slice(0, COLLAPSE_THRESHOLD).replace(/\s+\S*$/, "") + "…"
      : content;

  if (!sectionTitle && !content) {
    console.log("[TileReport] → null (no title/content)", { sectionKey });
    return null;
  }

  return (
    <div
      className="border border-surfaceBorder border-t-2 border-t-sky-400 overflow-hidden relative bg-reortTileBg scroll-mt-20 rounded-xl"
      style={tileCardStyle ? tileCardStyle(t) : undefined}
      {...(tileHoverHandlers ? tileHoverHandlers(t) : {})}
    >
      <div className="px-5 pt-4 pb-3 relative">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3 className="text-[14px] font-semibold text-zinc-800 dark:text-zinc-100 leading-snug flex-1 min-w-0">
            {sectionTitle}
          </h3>
          {versions.length > 1 && (
            <VersionDropdown
              versions={versions}
              selectedIdx={selectedIdx}
              onSelect={(idx) => onSelectVersion?.(idx)}
              t={t}
            />
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {versions.length <= 1 && timestamp && (
            <span style={{ color: t.textMuted }} className="text-[11px]">
              {timestamp}
            </span>
          )}
          {!!previous && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowChanges((v) => !v);
              }}
              style={{
                background: showChanges ? "rgba(14,165,233,0.1)" : t.labelBg,
                border: `1px solid ${showChanges ? "rgba(14,165,233,0.4)" : t.labelBorder}`,
                color: showChanges ? "#0ea5e9" : t.labelText,
              }}
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium transition-all"
            >
              <GitCompareArrows className="h-3 w-3" />
              Changes
            </button>
          )}
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${t.divider}` }} className="mx-5" />
      <div className="px-5 pt-3 pb-1 relative">
        {showChanges && diffParts ? (
          <DiffView diffParts={diffParts} t={t} />
        ) : (
          <div>
            {renderMarkdown(displayContent)}
            {isLong && !expanded && !showChanges && (
              <div className="relative mt-[-20px] h-8 pointer-events-none bg-gradient-to-b from-transparent to-reortTileBg" />
            )}
          </div>
        )}
      </div>
      <div className="px-5 pb-4 pt-1 flex flex-col gap-2">
        {selected.sources_list?.length > 0 && (
          <SourcesDropdown
            sourcesList={selected.sources_list}
            t={t}
            accent={accent}
          />
        )}
        {isLong && !showChanges && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            style={{ color: accent }}
            className="flex items-center gap-1 text-[12px] font-medium hover:opacity-75 transition-opacity self-start"
          >
            {expanded ? (
              <>
                <ChevronUp className="h-3.5 w-3.5" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-3.5 w-3.5" />
                Show more
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default TileReport;
